import React, { useState, useEffect, useMemo } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { usePlots } from "../context/PlotContext";
import { fetchVisits, fetchEnums, bookVisit, cancelVisit, fetchVisitAvailability } from "../api/api";

function formatSlotTime(t) {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

// Friendly labels for known VisitPurpose values — GET /meta/enums is the
// source of truth for which values are valid, not this map. Same fallback
// pattern as the mobile app's PURPOSE_LABELS.
const PURPOSE_LABELS = {
  farm_visit: "Farm visit",
  collect_harvest: "Collect Harvest",
  construction_review: "Construction Review",
  meet_resident: "Meet resident",
  visit_resort: "Visit resort",
};

const STATUS_STYLES = {
  pending: { background: "#fef3c7", color: "#92400e" },
  confirmed: { background: "#dcfce7", color: "#166534" },
  cancelled: { background: "#fee2e2", color: "#991b1b" },
  rescheduled: { background: "#dbeafe", color: "#1e40af" },
  completed: { background: "#e5e7eb", color: "#374151" },
};

function purposeLabel(purpose) {
  return PURPOSE_LABELS[purpose] || purpose;
}

function errorMessage(err) {
  const detail = err.response?.data?.detail;
  return detail || "Something went wrong. Please try again.";
}

function maxDateIso(daysAhead) {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  return d.toISOString().slice(0, 10);
}

// Visits need at least 24 hours' notice, so the earliest bookable date is
// the day after tomorrow, not tomorrow - matches the mobile app's
// MIN_LEAD_DAYS in VisitScreen.js.
const MIN_LEAD_DAYS = 2;
function minDateIso() {
  return maxDateIso(MIN_LEAD_DAYS);
}

function CustomerVisits() {

  const { user } = useAuth();

  const { plots, selectedPlot: plot, selectedPlotId, selectPlot, loading: plotsLoading } = usePlots();
  const [visits, setVisits] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [visitDate, setVisitDate] = useState(minDateIso());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selSlot, setSelSlot] = useState(null);
  const [purpose, setPurpose] = useState("");
  const [guestCount, setGuestCount] = useState(1);
  const [notes, setNotes] = useState("");
  const [visitorType, setVisitorType] = useState("self"); // 'self' | 'guest'
  const [visitorName, setVisitorName] = useState("");
  const [visitorEmail, setVisitorEmail] = useState("");
  const [visitorMobile, setVisitorMobile] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");

  const [booking, setBooking] = useState(false);
  const [bookError, setBookError] = useState("");
  const [bookSuccess, setBookSuccess] = useState("");

  const [cancellingId, setCancellingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [visitList, enums] = await Promise.all([fetchVisits(), fetchEnums()]);
      setVisits(visitList);
      const purposeValues = enums.visit_purpose || [];
      setPurposes(purposeValues);
      setPurpose((prev) => prev || purposeValues[0] || "");
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Real slot availability from the backend — refetched whenever the plot
  // or selected date changes, so a slot already booked by anyone else on
  // this plot shows as unavailable rather than a client-side guess. Same
  // endpoint and behavior as the mobile app's VisitScreen.
  useEffect(() => {
    if (!plot) return;
    let cancelled = false;
    setSlotsLoading(true);
    setSelSlot(null);
    fetchVisitAvailability(plot.id, visitDate)
      .then((data) => {
        if (cancelled) return;
        const fetchedSlots = data.slots || [];
        setSlots(fetchedSlots);
        const firstAvailable = fetchedSlots.findIndex((s) => s.available);
        setSelSlot(firstAvailable >= 0 ? firstAvailable : null);
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setSlotsLoading(false);
      });
    return () => { cancelled = true; };
  }, [plot, visitDate]);

  const upcoming = useMemo(
    () =>
      visits
        .filter((v) => v.status === "pending" || v.status === "confirmed" || v.status === "rescheduled")
        .sort((a, b) => a.visit_date.localeCompare(b.visit_date)),
    [visits]
  );

  const handleBook = async (e) => {
    e.preventDefault();
    setBookError("");
    setBookSuccess("");

    if (!plot) {
      setBookError("You don't have a registered plot yet.");
      return;
    }
    if (!purpose) {
      setBookError("Please select a purpose.");
      return;
    }
    if (visitorType === "guest" && (!visitorName.trim() || !visitorEmail.trim() || !visitorMobile.trim())) {
      setBookError("Please enter the guest's name, email, and mobile number.");
      return;
    }
    if (selSlot === null || !slots[selSlot]) {
      setBookError("Please choose an available time slot.");
      return;
    }

    const slot = slots[selSlot];
    setBooking(true);
    try {
      await bookVisit({
        plot_id: plot.id,
        visit_date: visitDate,
        start_time: slot.start_time,
        end_time: slot.end_time,
        purpose,
        notes: notes.trim() || undefined,
        guest_count: Number(guestCount) || 1,
        visitor_type: visitorType,
        visitor_name: visitorType === "guest" ? visitorName.trim() : undefined,
        visitor_email: visitorType === "guest" ? visitorEmail.trim() : undefined,
        visitor_mobile: visitorType === "guest" ? visitorMobile.trim() : undefined,
        vehicle_number: vehicleNumber.trim() || undefined,
      });
      setNotes("");
      setVisitorType("self");
      setVisitorName("");
      setVisitorEmail("");
      setVisitorMobile("");
      setVehicleNumber("");
      setBookSuccess("Your visit request has been submitted — it's pending confirmation from Jain Farms, which can take up to 8 hours. You'll get an email once it's confirmed.");
      await load();
    } catch (err) {
      setBookError(errorMessage(err));
    } finally {
      setBooking(false);
    }
  };

  const handleCancel = async (visitId) => {
    setCancellingId(visitId);
    try {
      await cancelVisit(visitId);
      await load();
    } catch (err) {
      setBookError(errorMessage(err));
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <>
      <Navbar />
      <Sidebar />

      <div className="main-content">

        <div className="customer-visit-page">

          {loading && <p>Loading…</p>}
          {loadError && <p className="login-error" style={{ color: "red" }}>{loadError}</p>}

          {!loading && !loadError && (
            <>

              <div className="visit-card">
                <h1>Upcoming Visits</h1>

                {upcoming.length === 0 && <p style={{ color: "#666" }}>No upcoming visits booked yet.</p>}

                {upcoming.map((v) => (
                  <div
                    key={v.id}
                    style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "12px 0", borderBottom: "1px solid #eee",
                    }}
                  >
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <strong>{purposeLabel(v.purpose)}</strong>
                        <span
                          style={{
                            ...(STATUS_STYLES[v.status] || {}),
                            padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600,
                          }}
                        >
                          {v.status}
                        </span>
                      </div>
                      <div style={{ fontSize: "13px", color: "#888", marginTop: "4px" }}>
                        {v.confirmation_number ? `${v.confirmation_number} · ` : ""}
                        {v.visit_date} · {v.start_time} – {v.end_time}
                      </div>
                      {v.visitor_type === "guest" && v.visitor_name && (
                        <div style={{ fontSize: "13px", color: "#888" }}>
                          Guest: {v.visitor_name}{v.vehicle_number ? ` · ${v.vehicle_number}` : ""}
                        </div>
                      )}
                    </div>

                    <button
                      className="view-btn"
                      onClick={() => handleCancel(v.id)}
                      disabled={cancellingId === v.id}
                      style={{ background: "#dc2626", color: "white" }}
                    >
                      {cancellingId === v.id ? "…" : "Cancel"}
                    </button>
                  </div>
                ))}
              </div>

              <div className="visit-card">

                <h1>Schedule a Visit</h1>

                {!plot && !plotsLoading && (
                  <p style={{ color: "#991b1b" }}>You don't have a registered plot yet — contact Jain Farms to get one set up before booking a visit.</p>
                )}

                {plots.length > 1 && (
                  <div style={{ marginBottom: "10px" }}>
                    <label style={{ fontWeight: 600, marginRight: "8px" }}>Plot</label>
                    <select value={selectedPlotId || ""} onChange={(e) => selectPlot(Number(e.target.value))}>
                      {plots.map((p) => <option key={p.id} value={p.id}>{p.plot_number}</option>)}
                    </select>
                  </div>
                )}

                {bookError && <p className="login-error" style={{ color: "red" }}>{bookError}</p>}
                {bookSuccess && <p style={{ color: "#166534", fontWeight: 600 }}>{bookSuccess}</p>}

                <form onSubmit={handleBook}>

                  <label>Visit Date</label>
                  <input
                    type="date"
                    value={visitDate}
                    min={minDateIso()}
                    max={maxDateIso(60)}
                    onChange={(e) => setVisitDate(e.target.value)}
                    required
                  />

                  <label>Preferred Time Slot</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" }}>
                    {slotsLoading && <p style={{ color: "#666", margin: 0 }}>Loading slots…</p>}
                    {!slotsLoading && slots.length === 0 && (
                      <p style={{ color: "#666", margin: 0 }}>No slots configured for this date.</p>
                    )}
                    {!slotsLoading && slots.map((sl, i) => (
                      <button
                        type="button"
                        key={i}
                        className={i === selSlot ? "login-btn" : "register-btn"}
                        style={{
                          width: "auto", padding: "10px 16px", textAlign: "left",
                          opacity: sl.available ? 1 : 0.5,
                          cursor: sl.available ? "pointer" : "not-allowed",
                        }}
                        disabled={!sl.available}
                        onClick={() => setSelSlot(i)}
                      >
                        {formatSlotTime(sl.start_time)} – {formatSlotTime(sl.end_time)}
                        {!sl.available && "  (Full)"}
                      </button>
                    ))}
                  </div>

                  <label>Purpose</label>
                  <select value={purpose} onChange={(e) => setPurpose(e.target.value)} required>
                    <option value="" disabled>Select purpose</option>
                    {purposes.map((p) => <option key={p} value={p}>{purposeLabel(p)}</option>)}
                  </select>

                  <label>Visiting as</label>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                    <button
                      type="button"
                      className={visitorType === "self" ? "login-btn" : "register-btn"}
                      style={{ width: "auto", padding: "8px 20px" }}
                      onClick={() => setVisitorType("self")}
                    >
                      Self
                    </button>
                    <button
                      type="button"
                      className={visitorType === "guest" ? "login-btn" : "register-btn"}
                      style={{ width: "auto", padding: "8px 20px" }}
                      onClick={() => setVisitorType("guest")}
                    >
                      Guest
                    </button>
                  </div>

                  {visitorType === "self" ? (
                    <div style={{ background: "#f5f2ec", borderRadius: "10px", padding: "12px", marginBottom: "12px", fontSize: "13px" }}>
                      <div>👤 {user?.full_name || "—"}</div>
                      <div>📧 {user?.email || "—"}</div>
                      <div>📱 {user?.mobile || "—"}</div>
                    </div>
                  ) : (
                    <>
                      <label>Guest Name</label>
                      <input type="text" value={visitorName} onChange={(e) => setVisitorName(e.target.value)} required />

                      <label>Guest Email</label>
                      <input type="email" value={visitorEmail} onChange={(e) => setVisitorEmail(e.target.value)} required />

                      <label>Guest Mobile</label>
                      <input type="tel" value={visitorMobile} onChange={(e) => setVisitorMobile(e.target.value)} required />
                    </>
                  )}

                  <label>Vehicle Number (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. KA05AB1234"
                    value={vehicleNumber}
                    onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                  />

                  <label>Number of Visitors</label>
                  <input
                    type="number"
                    min="1"
                    value={guestCount}
                    onChange={(e) => setGuestCount(e.target.value)}
                  />

                  <label>Notes (optional)</label>
                  <textarea
                    rows="3"
                    placeholder="Anything the team should know"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />

                  <button type="submit" className="submit-visit-btn" disabled={booking || !plot || selSlot === null}>
                    {booking ? "Booking…" : "Schedule Visit"}
                  </button>

                  <p className="visit-note">
                    Your visit will be <strong>pending confirmation from Jain Farms</strong> (up to 8 hours) — you'll get an email once it's confirmed.
                  </p>
                </form>

              </div>

            </>
          )}

        </div>

      </div>
    </>
  );
}

export default CustomerVisits;
